import { TextInputV2 } from '@porterin/component-library/src/base-components';
import { convertPlaceToAddressDetails } from '@porterin/component-library/src/composite-components/google-auto-complete/GoogleAutoCompleteHelper';
import { Autocomplete } from '@react-google-maps/api';
import React, { useState, useRef } from 'react';

type primaryAddress = {
  landmark: string;
  location: { lat: number; lng: number };
};

type postalAddress = {
  pincode?: string;
  state?: string;
  city?: string;
  locality?: string;
  sub_locality?: string;
};

export type AddressDetails = {
  primaryAddress: primaryAddress;
  postalAddress: postalAddress;
};

type inputOptions = {
  disabled?: boolean;
  placeholder?: string;
  error?: string;
  label: string;
  required?: boolean;
};

type identifiers = {
  id: string;
};

type inputIcons = {
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
};

type restrictions = {
  country: string;
};

interface GoogleMapsAutocompleteProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  identifiers: identifiers;
  onLocationSelect: (addressDetails: AddressDetails) => void;
  inputOptions?: inputOptions;
  icons?: inputIcons;
  restrictions?: restrictions;
  className?: string;
  showClearIcon?: boolean;
  onClick?: () => void;
}

const GoogleMapsAutocomplete: React.FC<GoogleMapsAutocompleteProps> = ({
  identifiers,
  onLocationSelect,
  inputOptions,
  icons,
  restrictions = { country: 'IN' },
  className,
  showClearIcon = false,
  onClick,
  ...props
}) => {
  
  const [value, setValue] = useState<string>(String(props.value ?? ''));
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    const currentInputValue = inputRef.current?.value ?? '';

    const place = autocompleteRef.current.getPlace();

    if (!place || !place.geometry || !place.formatted_address) {
      console.warn('⚠️ Invalid place selected. Ignoring...');
      return;
    }

    let displayValue = currentInputValue || place.formatted_address;
    let landmarkValue = currentInputValue || place.formatted_address;

    if (
      !currentInputValue &&
      place.name &&
      place.name !== place.formatted_address
    ) {
      displayValue = `${place.name}, ${place.formatted_address}`;
      landmarkValue = displayValue;
    }

    setValue(displayValue);

    const addressDetails = convertPlaceToAddressDetails(place);
    addressDetails.primaryAddress.landmark = landmarkValue;
    onLocationSelect(addressDetails);
  };

  return (
    <Autocomplete
      onLoad={autocomplete => (autocompleteRef.current = autocomplete)}
      onPlaceChanged={handlePlaceSelect}
      options={{ componentRestrictions: { country: restrictions.country } }}
    >
      <TextInputV2
        ref={inputRef}
        identifiers={identifiers}
        interactions={{
          onChange: e => setValue(e.target.value),
        }}
        value={value}
        onClick={onClick}
        inputOptions={inputOptions}
        className={className}
        {...props}
      />
    </Autocomplete>
  );
};

export default GoogleMapsAutocomplete;
